import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Shield, Mail, Send, ArrowLeft, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import supportAxios from '../../utils/supportAxios';

const SupportForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    setLoading(true);
    try {
      const res = await supportAxios.post('/support/api/forgot-password', { email: email.trim() });
      if (res.data.status === 'success') {
        setSent(true);
      } else {
        setError(res.data.message || 'Failed to send reset code.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send reset code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const goToVerify = () => {
    navigate(`/support/verify-reset-code?email=${encodeURIComponent(email.trim())}`);
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-purple-50 flex items-center justify-center px-4">
        <div className="w-full max-w-sm bg-white rounded-2xl shadow-lg p-8 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-800 mb-2">Check Your Email</h1>
          <p className="text-gray-600 mb-4">
            If an account exists for <span className="font-medium text-purple-700">{email.trim()}</span>,
            a 6-digit verification code has been sent to it.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            The code expires in 15 minutes. Didn't receive it? Check your spam folder.
          </p>
          <button
            onClick={goToVerify}
            className="w-full py-2.5 bg-gradient-to-r from-[--asfi-blue] to-[--asfi-purple] text-white rounded-lg font-medium hover:opacity-95 transition"
          >
            Enter Verification Code
          </button>
          <button
            onClick={() => setSent(false)}
            className="w-full mt-3 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition"
          >
            Use a different email
          </button>
          <div className="mt-4">
            <Link to="/support/login" className="text-sm text-purple-600 hover:underline flex items-center justify-center gap-1">
              <ArrowLeft className="w-4 h-4" /> Back to login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-purple-50 flex items-center justify-center px-4">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-lg p-8">
        <div className="flex items-center justify-center gap-2 mb-6">
          <div className="w-10 h-10 bg-gradient-to-br from-[--asfi-blue] to-[--asfi-purple] rounded-lg flex items-center justify-center">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <h1 className="text-xl font-semibold text-gray-800">Forgot Password</h1>
        </div>

        {error && (
          <div className="mb-4 flex items-center text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-2">
            <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-transparent outline-none"
                placeholder="support@asfirj.org"
                required
                disabled={loading}
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 bg-gradient-to-r from-[--asfi-blue] to-[--asfi-purple] text-white rounded-lg font-medium hover:opacity-95 disabled:opacity-60 transition flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                Sending code...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Send Reset Code
              </>
            )}
          </button>
        </form>

        <div className="mt-4">
          <Link to="/support/login" className="text-sm text-purple-600 hover:underline flex items-center justify-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Back to login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SupportForgotPassword;